import {
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverClose,
  PopoverTrigger,
} from "@clarkinc/zest-design-system/ui";
import React, {
  FocusEventHandler,
  MouseEventHandler,
  MutableRefObject,
  useCallback,
  useRef,
  useState,
} from "react";

interface Props {
  accessoryName: string;
  extendedInfo: string;
  placement?: "left" | "right" | "bottom" | "top";
  portalContainer?: HTMLElement;
}

const AccessoryPopover = ({
  accessoryName,
  extendedInfo,
  placement,
  portalContainer,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [wasAutoFocusedOnClose, setWasAutoFocusedOnClose] = useState(false);
  const mouseOverRef: MutableRefObject<HTMLElement | null> =
    useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleMouseEnter: MouseEventHandler<
    HTMLButtonElement | HTMLDivElement
  > = useCallback((e): void => {
    mouseOverRef.current = e.target as HTMLElement;
    setOpen(true);
  }, []);

  const handleMouseTriggerLeave = useCallback((): void => {
    if (contentRef.current?.contains(mouseOverRef.current)) {
      return;
    }
    setOpen(false);
  }, [contentRef]);

  const handleMouseContentLeave = useCallback((): void => {
    if (triggerRef.current?.contains(mouseOverRef.current)) {
      return;
    }
    setOpen(false);
  }, [triggerRef]);

  const handleFocus: FocusEventHandler<HTMLButtonElement> = useCallback(() => {
    if (wasAutoFocusedOnClose) {
      setWasAutoFocusedOnClose(false);
      setOpen(false);
      return;
    }
    setOpen(true);
  }, [wasAutoFocusedOnClose]);

  const handleCloseAutoFocus = (event: Event) => {
    event.preventDefault();
    setOpen(false);
    setWasAutoFocusedOnClose(true);
    setTimeout(() => {
      triggerRef.current?.focus();
    }, 0);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-label={`more info about ${accessoryName}`}
        className="border-0 bg-transparent"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseTriggerLeave}
        onFocus={handleFocus}
        ref={triggerRef}
      >
        <Icon
          theme="wss"
          className="fill-green-500"
          data-testid="icon-question"
          name="ri-question-fill"
          size="xsmall"
        />
      </PopoverTrigger>
      <PopoverContent
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseContentLeave}
        onCloseAutoFocus={(e) => {
          handleCloseAutoFocus(e);
        }}
        ref={contentRef}
        placement={placement}
        portalContainer={portalContainer}
      >
        <PopoverClose aria-label={`close more info about ${accessoryName}`} />
        <PopoverBody>
          {}
          <span dangerouslySetInnerHTML={{ __html: extendedInfo }} />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
export default AccessoryPopover;
